import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  DollarSign,
  UserCheck,
  TrendingUp,
  FolderOpen,
  Clock,
  Save,
  Loader2,
  ExternalLink,
  CalendarDays,
  Filter,
  X,
  Mail,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { format } from "date-fns";

const fmt$ = (v) => {
  if (!v && v !== 0) return "$0";
  return "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtShort$ = (v) => {
  if (!v && v !== 0) return "$0";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const formatDateSafe = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const s = String(dateStr);
    const part = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
    const [y, m, d] = part.split("-").map(Number);
    if (!y || !m || !d) return "—";
    return format(new Date(y, m - 1, d), "MMM d, yyyy");
  } catch {
    return "—";
  }
};

const STATUS_STYLES = {
  closed: { bg: "#05966818", color: "#059669", label: "Closed" },
  active: { bg: "#2563eb18", color: "#2563eb", label: "Active" },
  through_inspections: { bg: "#D9770618", color: "#D97706", label: "Through Inspections" },
  pending: { bg: "#7c3aed18", color: "#7c3aed", label: "Pending" },
};

const CoordinatorEarnings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [earningsData, setEarningsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCoordinator, setSelectedCoordinator] = useState("");
  const [feeInput, setFeeInput] = useState("");
  const [savingFee, setSavingFee] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [closedAgentFilter, setClosedAgentFilter] = useState("all");
  const [pendingAgentFilter, setPendingAgentFilter] = useState("all");

  // Date range
  const currentYear = new Date().getFullYear();
  const [dateFrom, setDateFrom] = useState(`${currentYear}-01-01`);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split("T")[0]);
  const [activeFrom, setActiveFrom] = useState(`${currentYear}-01-01`);
  const [activeTo, setActiveTo] = useState(new Date().toISOString().split("T")[0]);

  const fetchEarnings = useCallback(async (coordId, from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (coordId) params.coordinator_id = coordId;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const res = await axios.get(`${API}/reports/coordinator-earnings`, {
        withCredentials: true,
        params,
      });
      setEarningsData(res.data);
      setFeeInput(String(res.data.coordinator?.fee_per_transaction || ""));
    } catch {
      toast.error("Failed to load earnings data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEarnings(selectedCoordinator, activeFrom, activeTo);
  }, [selectedCoordinator, activeFrom, activeTo, fetchEarnings]);

  const handleSaveFee = async () => {
    const fee = parseFloat(feeInput);
    if (isNaN(fee) || fee < 0) {
      toast.error("Please enter a valid fee amount");
      return;
    }
    setSavingFee(true);
    try {
      await axios.put(`${API}/reports/coordinator-fee`, {
        fee_per_transaction: fee,
        coordinator_id: selectedCoordinator || undefined,
      }, { withCredentials: true });
      toast.success("Fee updated successfully");
      fetchEarnings(selectedCoordinator, activeFrom, activeTo);
    } catch {
      toast.error("Failed to update fee");
    } finally {
      setSavingFee(false);
    }
  };

  const handleSendPreview = async () => {
    setSendingPreview(true);
    try {
      const res = await axios.post(`${API}/reports/send-monthly-earnings-preview`, {}, { withCredentials: true });
      toast.success(res.data.message || "Monthly earnings email sent!");
    } catch {
      toast.error("Failed to send earnings preview email");
    } finally {
      setSendingPreview(false);
    }
  };

  const applyFilter = () => {
    setActiveFrom(dateFrom);
    setActiveTo(dateTo);
  };

  const setYTD = () => {
    const f = `${currentYear}-01-01`;
    const t = new Date().toISOString().split("T")[0];
    setDateFrom(f);
    setDateTo(t);
    setActiveFrom(f);
    setActiveTo(t);
  };

  const setPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const f = from.toISOString().split("T")[0];
    const t = to.toISOString().split("T")[0];
    setDateFrom(f);
    setDateTo(t);
    setActiveFrom(f);
    setActiveTo(t);
  };

  const s = earningsData?.summary || {};
  const coord = earningsData?.coordinator || {};
  const coordinators = earningsData?.coordinators || [];
  const agents = earningsData?.agents || [];

  const matchesAgent = (file, filter) => {
    if (filter === "all") return true;
    return (file.buyer_agent_name || "").trim() === filter || (file.seller_agent_name || "").trim() === filter;
  };

  const filteredClosed = (earningsData?.closed_files || []).filter(f => matchesAgent(f, closedAgentFilter));
  const filteredPending = (earningsData?.pending_files || []).filter(f => matchesAgent(f, pendingAgentFilter));

  const AgentDropdown = ({ value, onChange, testId }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-xs w-[180px] bg-[var(--t-bg)]" data-testid={testId}>
        <Users className="w-3 h-3 mr-1 flex-shrink-0" />
        <SelectValue placeholder="All Agents" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Agents</SelectItem>
        {agents.map((a) => (
          <SelectItem key={a} value={a}>{a}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <section data-testid="section-coordinator-earnings" className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4 pt-2">
        <button onClick={() => document.getElementById("reports-top")?.scrollIntoView({ behavior: "smooth" })} className="flex items-center gap-3 group text-left cursor-pointer" data-testid="section-header-earnings">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--t-primary)", color: "#fff" }}>
            <DollarSign className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base font-bold group-hover:underline" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }}>
              Coordinator Earnings
            </h2>
            <p className="text-xs" style={{ color: "var(--t-text-muted)" }}>
              Track closed deals and earnings by coordinator
            </p>
          </div>
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSendPreview}
          disabled={sendingPreview}
          className="text-xs"
          data-testid="send-earnings-preview-btn"
        >
          {sendingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
          Email My Summary
        </Button>
      </div>

      {/* Controls Row */}
      <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg" style={{ background: "var(--t-bg)", border: "1px solid var(--t-border)" }}>
        {/* Coordinator Selector (admin only) */}
        {isAdmin && coordinators.length > 0 && (
          <div>
            <Label className="text-[10px] uppercase tracking-wider block mb-0.5" style={{ color: "var(--t-text-subtle)" }}>Coordinator</Label>
            <Select value={selectedCoordinator} onValueChange={setSelectedCoordinator}>
              <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="coordinator-select">
                <SelectValue placeholder="Select coordinator..." />
              </SelectTrigger>
              <SelectContent>
                {coordinators.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>
                    <span className="flex items-center gap-2">
                      {c.name}
                      <span className="text-[10px] opacity-60 capitalize">({c.role})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Fee Input */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider block mb-0.5" style={{ color: "var(--t-text-subtle)" }}>
            Fee per Transaction
          </Label>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs" style={{ color: "var(--t-text-muted)" }}>$</span>
              <Input
                type="number"
                min="0"
                step="50"
                value={feeInput}
                onChange={(e) => setFeeInput(e.target.value)}
                className="h-8 text-xs w-[120px] pl-5"
                placeholder="0.00"
                data-testid="fee-input"
              />
            </div>
            <Button
              size="sm"
              className="h-8 text-xs bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]"
              onClick={handleSaveFee}
              disabled={savingFee}
              data-testid="save-fee-btn"
            >
              {savingFee ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        {/* Date Range */}
        <div className="flex items-end gap-2 ml-auto">
          <div>
            <Label className="text-[10px] uppercase tracking-wider block mb-0.5" style={{ color: "var(--t-text-subtle)" }}>From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-[140px]" data-testid="earnings-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider block mb-0.5" style={{ color: "var(--t-text-subtle)" }}>To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-[140px]" data-testid="earnings-date-to" />
          </div>
          <Button size="sm" className="h-8 text-xs bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" onClick={applyFilter} data-testid="apply-earnings-filter">
            <Filter className="w-3 h-3 mr-1" /> Apply
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[10px] px-2" onClick={setYTD} data-testid="earnings-ytd-btn">
            YTD
          </Button>
          {[
            { label: "Q1", days: 90 },
            { label: "6mo", days: 180 },
          ].map((p) => (
            <Button key={p.label} variant="outline" size="sm" className="h-7 text-[10px] px-2" onClick={() => setPreset(p.days)} data-testid={`earnings-preset-${p.label}`}>
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--t-primary)" }} />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="earnings-summary-cards">
            <div className="p-4 rounded-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#05966818", color: "#059669" }}>
                  <UserCheck className="w-4.5 h-4.5" />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: "var(--t-text-muted)" }}>Closed Files</p>
                  <p className="text-xl font-bold mt-0.5" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }} data-testid="closed-count">{s.closed_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#05966818", color: "#059669" }}>
                  <DollarSign className="w-4.5 h-4.5" />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: "var(--t-text-muted)" }}>Total Earned</p>
                  <p className="text-xl font-bold mt-0.5" style={{ color: "#059669", fontFamily: "var(--t-heading-font)" }} data-testid="total-earned">{fmt$(s.total_earned)}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: "var(--t-text-subtle)" }}>{s.closed_count || 0} × {fmt$(coord.fee_per_transaction)}</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#2563eb18", color: "#2563eb" }}>
                  <FolderOpen className="w-4.5 h-4.5" />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: "var(--t-text-muted)" }}>Active Files</p>
                  <p className="text-xl font-bold mt-0.5" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }} data-testid="pending-count">{s.pending_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#2563eb18", color: "#2563eb" }}>
                  <TrendingUp className="w-4.5 h-4.5" />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: "var(--t-text-muted)" }}>Pending Volume</p>
                  <p className="text-xl font-bold mt-0.5" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }} data-testid="pending-volume">{fmtShort$(s.pending_volume)}</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#D9770618", color: "#D97706" }}>
                  <Clock className="w-4.5 h-4.5" />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: "var(--t-text-muted)" }}>Pending Earnings</p>
                  <p className="text-xl font-bold mt-0.5" style={{ color: "#D97706", fontFamily: "var(--t-heading-font)" }} data-testid="pending-earnings">{fmt$(s.pending_earnings)}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: "var(--t-text-subtle)" }}>If all close</p>
                </div>
              </div>
            </div>
          </div>

          {/* Closed Files Table */}
          <Card className="card-surface" data-testid="closed-files-table">
            <CardHeader className="border-b py-3 px-5 flex flex-row items-center justify-between" style={{ borderColor: "var(--t-border)" }}>
              <CardTitle className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--t-text-muted)" }}>
                <UserCheck className="w-3.5 h-3.5" /> Closed Files — {activeFrom} to {activeTo}
              </CardTitle>
              {agents.length > 0 && (
                <AgentDropdown value={closedAgentFilter} onChange={setClosedAgentFilter} testId="closed-agent-filter" />
              )}
            </CardHeader>
            <CardContent className="p-0">
              {filteredClosed.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm" style={{ color: "var(--t-text-muted)" }}>
                    {closedAgentFilter !== "all" ? `No closed files for ${closedAgentFilter}` : "No closed files in this date range"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left" style={{ borderBottom: "1px solid var(--t-border)" }}>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Property</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Agent</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Closing Date</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Price</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Fee Earned</th>
                        <th className="px-4 py-2.5 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClosed.map((f, i) => (
                        <tr key={f.transaction_id} className="hover:opacity-80 transition-opacity" style={{ borderBottom: "1px solid var(--t-border-light)" }} data-testid={`closed-file-${i}`}>
                          <td className="px-4 py-2.5 font-medium" style={{ color: "var(--t-text)" }}>{f.property_address || "—"}</td>
                          <td className="px-4 py-2.5 text-xs" style={{ color: "var(--t-text-muted)" }}>{[f.buyer_agent_name, f.seller_agent_name].filter(Boolean).join(" / ") || "—"}</td>
                          <td className="px-4 py-2.5 text-xs" style={{ color: "var(--t-text-muted)" }}>{formatDateSafe(f.closing_date)}</td>
                          <td className="px-4 py-2.5 text-xs font-medium" style={{ color: "var(--t-text)" }}>{fmtShort$(f.purchase_price)}</td>
                          <td className="px-4 py-2.5 font-semibold" style={{ color: "#059669" }}>{fmt$(coord.fee_per_transaction)}</td>
                          <td className="px-4 py-2.5">
                            <Link to={`/transactions/${f.transaction_id}`} title="View transaction" data-testid={`closed-link-${i}`}>
                              <ExternalLink className="w-3.5 h-3.5" style={{ color: "var(--t-text-subtle)" }} />
                            </Link>
                          </td>
                        </tr>
                      ))}
                      {/* Total row */}
                      <tr style={{ borderTop: "2px solid var(--t-border)" }}>
                        <td colSpan="3" className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>
                          Total Earned ({filteredClosed.length} file{filteredClosed.length !== 1 ? "s" : ""})
                        </td>
                        <td></td>
                        <td className="px-4 py-2.5 font-bold text-base" style={{ color: "#059669" }}>{fmt$(filteredClosed.length * (coord.fee_per_transaction || 0))}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Files Table */}
          <Card className="card-surface" data-testid="pending-files-table">
            <CardHeader className="border-b py-3 px-5 flex flex-row items-center justify-between" style={{ borderColor: "var(--t-border)" }}>
              <CardTitle className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--t-text-muted)" }}>
                <FolderOpen className="w-3.5 h-3.5" /> Active / Pending Files
              </CardTitle>
              {agents.length > 0 && (
                <AgentDropdown value={pendingAgentFilter} onChange={setPendingAgentFilter} testId="pending-agent-filter" />
              )}
            </CardHeader>
            <CardContent className="p-0">
              {filteredPending.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm" style={{ color: "var(--t-text-muted)" }}>
                    {pendingAgentFilter !== "all" ? `No active files for ${pendingAgentFilter}` : "No active files"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left" style={{ borderBottom: "1px solid var(--t-border)" }}>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Property</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Agent</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Status</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Closing Date</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Price</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Potential Fee</th>
                        <th className="px-4 py-2.5 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPending.map((f, i) => {
                        const st = STATUS_STYLES[f.status] || { bg: "#94a3b818", color: "#64748b", label: f.status };
                        return (
                          <tr key={f.transaction_id} className="hover:opacity-80 transition-opacity" style={{ borderBottom: "1px solid var(--t-border-light)" }} data-testid={`pending-file-${i}`}>
                            <td className="px-4 py-2.5 font-medium" style={{ color: "var(--t-text)" }}>{f.property_address || "—"}</td>
                            <td className="px-4 py-2.5 text-xs" style={{ color: "var(--t-text-muted)" }}>{[f.buyer_agent_name, f.seller_agent_name].filter(Boolean).join(" / ") || "—"}</td>
                            <td className="px-4 py-2.5">
                              <Badge className="text-[10px] font-medium" style={{ background: st.bg, color: st.color, border: "none" }}>{st.label}</Badge>
                            </td>
                            <td className="px-4 py-2.5 text-xs" style={{ color: "var(--t-text-muted)" }}>{formatDateSafe(f.closing_date)}</td>
                            <td className="px-4 py-2.5 text-xs font-medium" style={{ color: "var(--t-text)" }}>{fmtShort$(f.purchase_price)}</td>
                            <td className="px-4 py-2.5 font-medium" style={{ color: "#D97706" }}>{fmt$(coord.fee_per_transaction)}</td>
                            <td className="px-4 py-2.5">
                              <Link to={`/transactions/${f.transaction_id}`} title="View transaction" data-testid={`pending-link-${i}`}>
                                <ExternalLink className="w-3.5 h-3.5" style={{ color: "var(--t-text-subtle)" }} />
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                      {/* Total row */}
                      <tr style={{ borderTop: "2px solid var(--t-border)" }}>
                        <td colSpan="2" className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>
                          Total ({filteredPending.length} file{filteredPending.length !== 1 ? "s" : ""})
                        </td>
                        <td></td>
                        <td></td>
                        <td className="px-4 py-2.5 font-bold" style={{ color: "var(--t-text)" }}>{fmtShort$(filteredPending.reduce((sum, f) => sum + (f.purchase_price || 0), 0))}</td>
                        <td className="px-4 py-2.5 font-bold" style={{ color: "#D97706" }}>{fmt$(filteredPending.length * (coord.fee_per_transaction || 0))}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </section>
  );
};

export default CoordinatorEarnings;
